import React, { useContext, useEffect, useMemo, useState } from "react";

import { UserContext } from "../../UserContext";
import { fetchUserInfo } from "../../api/authService";
import { getInstructors } from "../../api/instructorsService";
import DarkModeToggle from "../Header/DarkModeToggle";
import { openPopup } from "../Common/popupUIStore";
import { pressPoint } from "../UI/glass";
import useStudentBalance from "../../hooks/useStudentBalance";
import { formatLei } from "../../api/balanceService";

import StaffProfileHead from "./StaffProfile/StaffProfileHead";
import { ownAvatarUrl, rememberAvatarChange } from "../../utils/userAvatar";

const asBool = (v) => {
   if (typeof v === "boolean") return v;
   if (typeof v === "number") return v === 1;
   const s = String(v || "")
      .trim()
      .toLowerCase();
   return s === "1" || s === "true" || s === "yes" || s === "da";
};

const asNumber = (v) => {
   const n = Number(v);
   return Number.isFinite(n) ? n : 0;
};

function readMedicalDocuments(src) {
   const values = [
      src?.medical_documents,
      src?.medicalDocuments,
      src?.extras?.medical_documents,
      src?.profile?.medical_documents,
   ];
   return values.some((v) => asBool(v));
}

function readIndividualWork(src) {
   const values = [
      src?.individual_work,
      src?.individualWork,
      src?.extras?.individual_work,
      src?.profile?.individual_work,
   ];
   return values.some((v) => asBool(v));
}

function readAbsences(src) {
   return asNumber(
      src?.number_of_absences ??
         src?.numberOfAbsences ??
         src?.extras?.number_of_absences ??
         src?.profile?.number_of_absences ??
         0,
   );
}

function readIdnp(src) {
   const raw =
      src?.idnp ?? src?.IDNP ?? src?.profile?.idnp ?? src?.extras?.idnp ?? "";
   return String(raw || "").trim();
}

function readDesiredInstructorId(src) {
   const raw = src?.desiredInstructorId ?? src?.desiredInstructor?.id ?? null;
   const n = Number(raw);
   return Number.isInteger(n) && n > 0 ? n : null;
}

function instructorFullName(inst) {
   if (!inst) return "";
   return `${inst?.firstName || ""} ${inst?.lastName || ""}`.trim();
}

export default function StudentSelfProfilePopup() {
   const { user, setUser } = useContext(UserContext);
   const balance = useStudentBalance();
   const [me, setMe] = useState(user || null);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");

   useEffect(() => {
      let alive = true;
      (async () => {
         setLoading(true);
         setError("");
         try {
            const fresh = await fetchUserInfo();
            if (!alive) return;
            setMe(fresh || user || null);
         } catch {
            if (!alive) return;
            setMe(user || null);
            setError("Nu am putut actualiza profilul din server.");
         } finally {
            if (alive) setLoading(false);
         }
      })();

      return () => {
         alive = false;
      };
   }, [user]);

   const fullName = useMemo(() => {
      const first = String(me?.firstName || "").trim();
      const last = String(me?.lastName || "").trim();
      const joined = `${first} ${last}`.trim();
      if (joined) return joined;
      const fallback = String(me?.name || me?.fullName || "").trim();
      return fallback || "Student";
   }, [me]);

   const idnp = useMemo(() => readIdnp(me), [me]);
   const medicalDocuments = useMemo(() => readMedicalDocuments(me), [me]);
   const individualWork = useMemo(() => readIndividualWork(me), [me]);
   const numberOfAbsences = useMemo(() => readAbsences(me), [me]);

   const desiredInstructorId = useMemo(
      () => readDesiredInstructorId(me),
      [me],
   );
   const embeddedInstructorName = useMemo(
      () => instructorFullName(me?.desiredInstructor),
      [me],
   );
   const [resolvedInstructorName, setResolvedInstructorName] = useState("");

   useEffect(() => {
      let alive = true;
      setResolvedInstructorName("");
      if (!desiredInstructorId || embeddedInstructorName) return undefined;
      (async () => {
         try {
            const list = await getInstructors();
            if (!alive) return;
            const hit = (Array.isArray(list) ? list : []).find(
               (it) => String(it?.id ?? it?._id) === String(desiredInstructorId),
            );
            setResolvedInstructorName(instructorFullName(hit));
         } catch {
            /* numele rămâne gol — rândul afișează doar dacă îl avem */
         }
      })();
      return () => {
         alive = false;
      };
   }, [desiredInstructorId, embeddedInstructorName]);

   const desiredInstructorName =
      embeddedInstructorName || resolvedInstructorName;
   const presentedMedicalLabel = medicalDocuments ? "Prezentat" : "Neprezentat";
   const presentedIndividualLabel = individualWork
      ? "Prezentat"
      : "Neprezentat";

   return (
      <div className="studentSelfProfilePopup">
         {/* antetul, ca la personal: avatarul (poza: meniul din creion — vezi / schimbă / șterge),
             numele, „Student”; poza nouă se vede pe loc și sus, în meniu */}
         <StaffProfileHead
            className="studentSelfProfilePopup__head"
            person={user}
            name={fullName}
            subtitle="Student"
            photo={
               user?.id
                  ? {
                       mode: "upload",
                       target: "me",
                       hasPhoto: Boolean(ownAvatarUrl(user)),
                       onChanged: (url) => {
                          rememberAvatarChange(user.id, url);
                          if (typeof setUser === "function") setUser((prev) => (prev ? { ...prev, avatarUrl: url } : prev));
                       },
                    }
                  : null
            }
         />

         <div className="studentSelfProfilePopup__content">
            {loading ? (
               <p className="studentSelfProfilePopup__state">Se încarcă...</p>
            ) : (
               <>
               <p className="studentSelfProfilePopup__section">Date personale</p>
               <div className="studentSelfProfilePopup__list">
                  <div className="studentSelfProfilePopup__row">
                     <span>Email</span>
                     <strong>{me?.email || "—"}</strong>
                  </div>
                  <div className="studentSelfProfilePopup__row">
                     <span>Telefon</span>
                     <strong>{me?.phone || "—"}</strong>
                  </div>
                  <div className="studentSelfProfilePopup__row">
                     <span>IDNP</span>
                     <strong>{idnp || "—"}</strong>
                  </div>
                  <div className="studentSelfProfilePopup__row">
                     <span>Documente medicale</span>
                     <strong>{presentedMedicalLabel}</strong>
                  </div>
                  <div className="studentSelfProfilePopup__row">
                     <span>Lucru individual</span>
                     <strong>{presentedIndividualLabel}</strong>
                  </div>
                  <div className="studentSelfProfilePopup__row">
                     <span>Absențe</span>
                     <strong>{numberOfAbsences}</strong>
                  </div>
                  {desiredInstructorId && desiredInstructorName ? (
                     <div className="studentSelfProfilePopup__row">
                        <span>Instructor preferat</span>
                        <strong>{desiredInstructorName}</strong>
                     </div>
                  ) : null}
               </div>

               {/* Finanțe: soldul (balanța pornită pe server); apăsat — „Balanța” cu operațiile */}
               <p className="studentSelfProfilePopup__section">Finanțe</p>
               <div className="studentSelfProfilePopup__list">
                  {balance.enabled ? (
                     <button
                        type="button"
                        className="studentSelfProfilePopup__row studentSelfProfilePopup__row--button"
                        onPointerDown={pressPoint}
                        onClick={() => openPopup("studentBalance")}
                     >
                        <span>Sold</span>
                        <strong>{formatLei(balance.balanceBani)}</strong>
                     </button>
                  ) : (
                     <div className="studentSelfProfilePopup__row">
                        <span>Sold</span>
                        <strong>{balance.loaded ? "Indisponibil" : "Se încarcă..."}</strong>
                     </div>
                  )}
               </div>
               </>
            )}

            {!!error && (
               <p className="studentSelfProfilePopup__error">{error}</p>
            )}
         </div>

         <div className="studentSelfProfilePopup__footer">
            <ul className=" studentSelfProfilePopup__settings settings">
               <DarkModeToggle />
            </ul>
         </div>
      </div>
   );
}
